import { HttpParams } from '@angular/common/http';
import { Component, ChangeDetectionStrategy, OnDestroy, OnInit, ChangeDetectorRef } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { FadeInOut } from '@app/animations/fade.animation';
import { StreamAdapter, StreamCardResponse } from '@modules/content-api/adapter/stream.adapter';
import { ContentAPIService } from '@modules/content-api/content-api.service';
import { StreamCardImageStyle } from '@modules/content-api/domain/image-styles/stream-card.style';
import { MagazineResolver } from '@modules/content-api/resolver/magazine.resolver';
import { TopicAndMagazineBreadcrumbResolver } from '@modules/content-api/resolver/topic.resolver';
import { MediaImageModel } from '@modules/media/models/media-image.model';
import { MediaStreamInterface, MediaStreamModel } from '@modules/media/models/media-stream.model';
import { MediaVideoModel, VideoStatus } from '@modules/media/models/media-video.model';
import { PlayerService } from '@modules/media/service/player.service';
import { SectionType } from '@modules/section/enums/section-type.enum';
import { BreadcrumbService } from '@modules/shared/services/breadcrumb.service';
import { DataLayerService } from '@modules/shared/services/data-layer.service';
import { LightboxService } from '@modules/shared/services/lightbox.service';
import { TranslateService } from '@ngx-translate/core';
import { Magazine } from '@pages/magazine-page/models/magazine.model';
import { BehaviorSubject, EMPTY, Subject } from 'rxjs';
import { catchError, map, take, takeUntil } from 'rxjs/operators';

@Component({
  selector: 'mb-magazine-page',
  templateUrl: './magazine-page.component.html',
  styleUrls: ['./magazine-page.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  animations: FadeInOut,
})
export class MagazinePageComponent implements OnInit, OnDestroy {
  public sectionType = SectionType;
  public activeStream$ = new BehaviorSubject<MediaStreamModel | null>(null);
  public magazine: Magazine;

  private destroy$ = new Subject<boolean>();

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private translateService: TranslateService,
    private playerService: PlayerService,
    private lightboxService: LightboxService,
    private cdr: ChangeDetectorRef,
    private apiService: ContentAPIService,
    private streamAdapter: StreamAdapter,
    private magazineResolver: MagazineResolver,
    private topicMagazineBreadcrumbResolver: TopicAndMagazineBreadcrumbResolver,
    private breadcrumbService: BreadcrumbService,
    private dataLayerService: DataLayerService
  ) {
    this.magazine = this.route.snapshot.data.magazine;
  }

  ngOnInit(): void {
    // re-render this page on route data changes
    this.route.data.pipe(takeUntil(this.destroy$)).subscribe((data) => {
      this.magazine = data.magazine;
      this.cdr.detectChanges();
      this.pushPageViewDataLayer();
    });

    // update active player state, when video parameter is added
    this.route.queryParamMap.pipe(map((pMap) => pMap.get('video'))).subscribe((videoId: string | null) => {
      this.updateActiveStream(videoId);
    });

    this.playerService.playerClosed$.pipe(takeUntil(this.destroy$)).subscribe(() => {
      this.closePlayer();
    });

    this.translateService.onLangChange.pipe(takeUntil(this.destroy$)).subscribe(() => {
      this.magazineResolver
        .resolve(this.route.snapshot)
        .pipe(take(1))
        .subscribe((magazine) => {
          if (magazine) {
            this.magazine = magazine;
            this.cdr.detectChanges();
            this.pushPageViewDataLayer();
          }
        });

      // manually update breadcrumbs
      this.topicMagazineBreadcrumbResolver
        .resolve(this.route.snapshot)
        .pipe(take(1))
        .subscribe((breadcrumbs) => {
          this.breadcrumbService.swapBreadcrumbs(breadcrumbs);
        });
    });
  }

  public closePlayer(): void {
    this.router.navigate([], {
      queryParams: {
        video: null,
      },
      queryParamsHandling: 'merge',
    });
    // unset active stream
    this.activeStream$.next(null);
  }

  public streamStopped(): void {
    if (this.activeStream$.value) {
      const stream = this.activeStream$.value;
      stream.videoStatus = VideoStatus.STOPPED;
      this.activeStream$.next(stream);
      this.cdr.detectChanges();
    }
    this.closePlayer();
  }

  public openMedia(media: MediaImageModel | MediaVideoModel): void {
    /**
     * this line below will change the routing when a media was opened and reflect the state in the url.
     * but it will also retrigger the resolver, so we need to disable it for now.
     */
    // this.router.navigate([{ outlets: { lightbox: [media.type, media.id] } }], { relativeTo: this.route });
    this.lightboxService.openMedia(media, {
      label: 'CONNECTED_PAGE',
      text: this.magazine.title,
      link: {
        url: (this.magazine.currentSlug === '/page/home' ? '/' : this.magazine.currentSlug) || '/',
      },
    });
  }

  public openMediaFromTranscript(resource: any): void {
    const slug = this.magazine.currentSlug === 'home' ? '' : this.magazine.currentSlug;
    const contextLink = {
      label: 'CONNECTED_STREAM',
      text: this.activeStream$.value?.title || 'Stream',
      link: {
        url: slug,
        queryParams: {
          video: this.activeStream$.value?.id,
        },
      },
    };
    this.lightboxService.openMedia(resource, contextLink);
  }

  private updateActiveStream(videoId?: string | null) {
    if (videoId) {
      /**
       * dynamically load the stream because it could be that its not
       * included in the sections
       */
      this.apiService
        .getResource(
          'streams',
          videoId,
          new HttpParams().appendAll({
            include: [
              'background',
              'player_config.transcript',
              'player_config.qa_form.selectable_contact_people',
            ].join(),
          })
        )
        .pipe(
          take(1),
          map((data) => this.streamAdapter.parse(data as StreamCardResponse, StreamCardImageStyle)),
          catchError(() => {
            // close the player if we can't load the stream
            this.closePlayer();
            return EMPTY;
          })
        )
        .subscribe((data) => {
          this.activeStream$.next(data ? new MediaStreamModel(data as MediaStreamInterface) : null);
        });
    }

    this.activeStream$.next(null);
  }

  private pushPageViewDataLayer() {
    this.dataLayerService.pushPageView({
      id: this.magazine.id,
      type: this.magazine.currentSlug === 'home' ? 'homepage' : 'media_special',
      name: this.magazine.title,
      publishDate: undefined,
      marsOriginId: undefined,
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next(true);
    this.destroy$.complete();
  }
}
